import JwtService from "@/core/services/JwtService";
import transformServiceUrl from "@/core/services/servicesForEnv";
import OAuthTokenManager from "@/core/services/OAuthTokenManager";
import { configuredBusTokens } from './BusTokenService';
import { BusTokenError, fetchWithBusRecovery } from './BusTokenManager';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

class ApiService {
  static vueInstance;
  static defaultHeaders = {
    "Accept": "application/json, text/plain, */*",
    "Accept-Encoding": "gzip, deflate, br",
    "Accept-Language": "es-419,es-US;q=0.9,es;q=0.8",
    "Connection": "keep-alive",
    /* "Access-Control-Allow-Headers": "Content-Type, Content-Length, Authorization, Accept, X-Requested-With",
    "Access-Control-Allow-Methods": "PUT, POST, GET, DELETE, OPTIONS",
    "Access-Control-Allow-Origin": "*", */
    // Content-Type se establece dinámicamente según el tipo de petición
  };
  static baseUrl = process.env.VUE_APP_API_URL_TOYOTA;
  static token_type = "";
  static access_token = "";
  static tfsm = 'tfsm'

  static getBusToken() {
    return configuredBusTokens(ApiService.validateTrustedDestination).get();
  }

  static getFreshBusToken() {
    return configuredBusTokens(ApiService.validateTrustedDestination).fresh();
  }

  static invalidateBusToken(rejected: string) {
    const manager = configuredBusTokens(ApiService.validateTrustedDestination);
    manager.invalidate(rejected);
  }

  static async fetchWithBusToken(url: string, options: RequestInit = {}): Promise<Response> {
    const base = process.env.VUE_APP_API_URL_TOYOTA_BUS;
    if (!base) throw new BusTokenError();
    const trusted = ApiService.validateTrustedDestination(url, base);
    const manager = configuredBusTokens(ApiService.validateTrustedDestination);
    return fetchWithBusRecovery(trusted, options, manager);
  }

  private static async sendRequest(url: string, options: RequestInit): Promise<Response> {
    if (process.env.VUE_APP_WHOSE_IS !== ApiService.tfsm) return fetch(url, options);
    // Existing callers validated this URL at the sink; retain the allowlist here too.
    const trusted = ApiService.assertSafeHttpSink(url);
    const manager = configuredBusTokens(ApiService.validateTrustedDestination);
    const response = await fetchWithBusRecovery(trusted, options, manager);
    // An explicit BUS invalid_token must not trigger user-session destruction.
    const challenge = response.headers.get('WWW-Authenticate') || '';
    if (response.status === 401 && /\bBearer\b/i.test(challenge) &&
        /\berror\s*=\s*"?invalid_token\b/i.test(challenge)) throw new BusTokenError();
    return response;
  }

  private static getAllowedOrigins(): Set<string> {
    const allowedOrigins = new Set<string>();

    const addOrigin = (rawUrl?: string) => {
      if (!rawUrl || typeof rawUrl !== 'string') return;
      try {
        allowedOrigins.add(new URL(rawUrl).origin);
      } catch (_) {
        // Ignorar URLs inválidas del entorno
      }
    };

    addOrigin(ApiService.baseUrl);
    addOrigin(process.env.VUE_APP_API_URL_TOYOTA as string);
    addOrigin(process.env.VUE_APP_API_URL_TOYOTA_BUS_TOKEN as string);

    // Orígenes locales: gateway nginx (:8000, paridad Ingress) + puertos legacy por MS
    const localOrigins = [
      'http://localhost:8000',
      'http://localhost:9001',
      'http://localhost:9002',
      'http://localhost:9004',
      'http://localhost:9005',
      'http://localhost:9006',
      'http://localhost:9007',
      'http://localhost:9010'
    ];
    localOrigins.forEach(addOrigin);

    return allowedOrigins;
  }

  private static enforceAllowedDestination(url: string): string {
    const urlObj = new URL(url);
    const allowedOrigins = ApiService.getAllowedOrigins();

    if (!allowedOrigins.has(urlObj.origin)) {
      throw new Error('Destino no permitido');
    }

    if (urlObj.username || urlObj.password) {
      throw new Error('Credenciales en URL no permitidas');
    }

    return urlObj.toString();
  }

  private static resolveValidatedRequestUrl(resource: string, slug?: string, fromOrigin?: boolean): string {
    ApiService.checkUrl(resource);
    const safeUrl = ApiService.buildSafeUrl(ApiService.baseUrl, resource, slug, fromOrigin);
    const validatedUrlForFetch = ApiService.validateUrlForFetch(safeUrl);
    return ApiService.enforceAllowedDestination(validatedUrlForFetch);
  }

  static validateTrustedDestination(url: string, expectedBaseUrl?: string): string {
    const validatedUrl = ApiService.validateUrlForFetch(url);
    const destinationUrl = new URL(validatedUrl);

    if (expectedBaseUrl) {
      const expectedOrigin = new URL(expectedBaseUrl).origin;
      if (destinationUrl.origin !== expectedOrigin) {
        throw new Error('Destino no permitido');
      }
      return destinationUrl.toString();
    }

    return ApiService.enforceAllowedDestination(destinationUrl.toString());
  }

  /**
   * Validación explícita en el sink para mitigar SSRF/Open Redirect.
   * Se ejecuta inmediatamente antes de fetch/xhr.open.
   */
  private static assertSafeHttpSink(url: string): string {
    const trustedUrl = ApiService.validateTrustedDestination(url);
    const parsedUrl = new URL(trustedUrl);
    const allowedOrigins = ApiService.getAllowedOrigins();

    if (!allowedOrigins.has(parsedUrl.origin)) {
      throw new Error('Destino no permitido');
    }

    if (parsedUrl.username || parsedUrl.password) {
      throw new Error('Credenciales en URL no permitidas');
    }

    return parsedUrl.toString();
  }

  static getconfig({ method, headers, ambience }) {
    let config: any = {
      method,
      headers,
    }

    // Establecer Content-Type solo para peticiones que envían datos JSON
    if (method === "POST" || method === "PUT") {
      config = {
        ...config,
        headers: {...config.headers, 'Content-Type': 'application/json'},
      }
    }
    // Para GET y DELETE, no establecer Content-Type (el navegador lo maneja automáticamente)

    // Configurar credentials solo para ambientes que no sean tfsm
    if (ambience !== `${ApiService.tfsm}`) {
      config = {
        ...config,
        credentials: 'include',
      }
    }

    return config;
  }

  static async init(app) {
    ApiService.vueInstance = app;
  }

  static async setHeader() {
    if (process.env.VUE_APP_WHOSE_IS == `${ApiService.tfsm}`) {
      try {
        const { access_token, token_type } = await OAuthTokenManager.getValidToken();
        ApiService.token_type = token_type;
        ApiService.access_token = access_token;
      } catch (error: unknown) {
        const reason = error instanceof Error ? error.message : 'Error desconocido';
        throw new Error(`Error de configuración: ${reason}`);
      }
    }
  }

  /**
   * Fetch con un solo reintento si el Bearer OAuth caducó (401).
   * No mezcla con logout de sesión JWT (eso sigue en handleResponse).
   */
  private static async fetchWithOAuthRetry(
    sinkSafeUrl: string,
    init: RequestInit,
    rebuildAuth: () => Promise<{ url: string; init: RequestInit }>,
    retried = false,
  ): Promise<Response> {
    const response = await fetch(sinkSafeUrl, init);
    if (
      process.env.VUE_APP_WHOSE_IS === `${ApiService.tfsm}` &&
      response.status === 401 &&
      !retried
    ) {
      OAuthTokenManager.invalidate();
      const next = await rebuildAuth();
      return ApiService.fetchWithOAuthRetry(next.url, next.init, rebuildAuth, true);
    }
    return response;
  }

  private static async prepareRequest(
    originalResource: string,
    method: string,
    slug?: string,
  ): Promise<{ url: string; headers: any; resource: string; fromOrigin: boolean }> {
    let resource = originalResource;
    let headers: any = { ...ApiService.defaultHeaders };
    let fromOrigin = false;

    if (process.env.VUE_APP_WHOSE_IS == `${ApiService.tfsm}`) {
      const prepared = await ApiService.setTimeHeders(originalResource, method);
      resource = prepared.newResource;
      headers = prepared.newHeaders;
      fromOrigin = prepared.fromOrigin;
    }

    const validatedUrlForFetch = ApiService.resolveValidatedRequestUrl(resource, slug, fromOrigin);
    const trustedUrlForRequest = ApiService.validateTrustedDestination(validatedUrlForFetch);
    const url = ApiService.assertSafeHttpSink(trustedUrlForRequest);
    return { url, headers, resource, fromOrigin };
  }

  static async setTimeHeders(resource, method) {
    try {
      await ApiService.setHeader();
      const { url: newResource, fromOrigin } = await transformServiceUrl(resource, `${ApiService.tfsm}`, method);
      const newHeaders = {
        ...ApiService.defaultHeaders,
        Authorization: `${ApiService.token_type} ${ApiService.access_token}`,
        pStrCookie: localStorage.pStrCookie ?? "",
      };

      return {
        newHeaders,
        newResource,
        fromOrigin
      }
    } catch (error: unknown) {
      const reason = error instanceof Error ? error.message : 'Error desconocido';
      throw new Error(`Error de configuración: ${reason}`);
    }
  }

  static async query(resource, params) {
    try {
      const originalResource = resource;
      const prepared = await ApiService.prepareRequest(originalResource, 'get');
      const buildInit = (headers: any): RequestInit => ({
        ...ApiService.getconfig({ method: 'GET', headers, ambience: process.env.VUE_APP_WHOSE_IS }),
        redirect: 'error',
      });
      const response = await ApiService.fetchWithOAuthRetry(
        prepared.url,
        buildInit(prepared.headers),
        async () => {
          const again = await ApiService.prepareRequest(originalResource, 'get');
          return { url: again.url, init: buildInit(again.headers) };
        },
      );
      return await ApiService.handleResponse(response);
    } catch (error) {
      throw new Error('Error en la solicitud');
    }
  }

  static async get(resource) {
    try {
      const originalResource = resource;
      const prepared = await ApiService.prepareRequest(originalResource, 'get');
      const buildInit = (headers: any): RequestInit => ({
        ...ApiService.getconfig({ method: 'GET', headers, ambience: process.env.VUE_APP_WHOSE_IS }),
        redirect: 'error',
      });
      const response = await ApiService.fetchWithOAuthRetry(
        prepared.url,
        buildInit(prepared.headers),
        async () => {
          const again = await ApiService.prepareRequest(originalResource, 'get');
          return { url: again.url, init: buildInit(again.headers) };
        },
      );
      return await ApiService.handleResponse(response);
    } catch (error: unknown) {
      const hasResponseData =
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        Boolean((error as { response?: { data?: unknown } }).response?.data);

      if (hasResponseData) {
        throw error;
      }

      const reason = error instanceof Error ? error.message : 'Error desconocido';
      throw new Error(`Error en la solicitud: ${reason}`);
    }
  }

  static async getDownload(resource) {
    try {
      const originalResource = resource;
      const prepared = await ApiService.prepareRequest(originalResource, 'get');
      const buildInit = (headers: any): RequestInit => ({
        ...ApiService.getconfig({ method: 'GET', headers, ambience: process.env.VUE_APP_WHOSE_IS }),
        redirect: 'error',
      });
      const response = await ApiService.fetchWithOAuthRetry(
        prepared.url,
        buildInit(prepared.headers),
        async () => {
          const again = await ApiService.prepareRequest(originalResource, 'get');
          return { url: again.url, init: buildInit(again.headers) };
        },
      );

      if (!response.ok) {
        throw new Error('Error en la descarga');
      }

      return await response.blob();
    } catch (error) {
      throw new Error('Error en la descarga');
    }
  }

  static async post(resource, params, formData = false, isCustomer = false) {
    try {
      const originalResource = resource;
      let headers: any = {
        ...ApiService.defaultHeaders,
      };
      let fromOrigin = false;
      let resolvedResource = resource;

      if (process.env.VUE_APP_WHOSE_IS == `${ApiService.tfsm}`) {
        const { newResource, newHeaders, fromOrigin: fo } = await this.setTimeHeders(originalResource, "post")
        resolvedResource = newResource;
        headers = newHeaders;
        fromOrigin = fo;
      }

      if (!formData) {
        if (process.env.VUE_APP_WHOSE_IS == `${ApiService.tfsm}`) {
          const { url: transformedResource } = await transformServiceUrl(resolvedResource, `${ApiService.tfsm}`, "post");
          resolvedResource = transformedResource;
        }

        if (!isCustomer && JwtService.getToken() &&
            (!resolvedResource.includes("/seguridad/hasPermission") || !resolvedResource.includes("/TfsmHasPermission"))) {
          params = { ...params, user_token: JwtService.getToken() };
        }

        const buildOnce = async () => {
          let res = originalResource;
          let hdrs: any = { ...ApiService.defaultHeaders };
          let fo = false;
          if (process.env.VUE_APP_WHOSE_IS == `${ApiService.tfsm}`) {
            const prepared = await ApiService.setTimeHeders(originalResource, 'post');
            res = prepared.newResource;
            hdrs = prepared.newHeaders;
            fo = prepared.fromOrigin;
            const { url: transformedResource } = await transformServiceUrl(res, `${ApiService.tfsm}`, 'post');
            res = transformedResource;
          }
          const validatedUrlForFetch = ApiService.resolveValidatedRequestUrl(res, undefined, fo);
          const trustedUrlForRequest = ApiService.validateTrustedDestination(validatedUrlForFetch);
          const sinkSafeUrl = ApiService.assertSafeHttpSink(trustedUrlForRequest);
          return {
            url: sinkSafeUrl,
            init: {
              ...ApiService.getconfig({ method: 'POST', headers: hdrs, ambience: process.env.VUE_APP_WHOSE_IS }),
              redirect: 'error' as RequestRedirect,
              body: JSON.stringify(params),
            },
          };
        };

        const first = await buildOnce();
        // Keep first-path parity: use already-resolved headers/url when possible
        const validatedUrlForFetch = ApiService.resolveValidatedRequestUrl(resolvedResource, undefined, fromOrigin);
        const trustedUrlForRequest = ApiService.validateTrustedDestination(validatedUrlForFetch);
        const sinkSafeUrl = ApiService.assertSafeHttpSink(trustedUrlForRequest);
        const response = await ApiService.fetchWithOAuthRetry(
          sinkSafeUrl,
          {
            ...ApiService.getconfig({ method: 'POST', headers, ambience: process.env.VUE_APP_WHOSE_IS }),
            redirect: 'error',
            body: JSON.stringify(params),
          },
          buildOnce,
        );
        return await ApiService.handleResponse(response);
      } else {
        return await this.handleFormDataPost(resolvedResource, params, fromOrigin);
      }
    } catch (error: any) {
      // Si el error ya tiene la estructura correcta (viene de handleResponse), lo re-lanzamos
      if (error.response?.data) {
        throw error;
      }
      // Solo lanzamos error genérico si no es un error HTTP manejado
      throw new Error('Error en la solicitud');
    }
  }

  static async handleFormDataPost(resource, params, fromOrigin = false) {
    const sendOnce = async (allowRetry: boolean) => {
      let bearer = '';
      if (process.env.VUE_APP_WHOSE_IS == `${ApiService.tfsm}`) {
        try {
          const tokenResp = await OAuthTokenManager.getValidToken();
          bearer = tokenResp?.access_token || '';
        } catch (e) {
          console.warn('No se pudo obtener token OAuth2 para TFSM', e);
        }
      }

      return new Promise((resolve, reject) => {
        try {
          const validatedUrlForFetch = ApiService.resolveValidatedRequestUrl(resource, undefined, fromOrigin);
          const trustedUrlForRequest = ApiService.validateTrustedDestination(validatedUrlForFetch);
          const sinkSafeUrl = ApiService.assertSafeHttpSink(trustedUrlForRequest);
          const xhr = new XMLHttpRequest();
          xhr.open('POST', sinkSafeUrl, true);
          xhr.setRequestHeader('Accept', 'application/json, text/plain, */*');

          if (process.env.VUE_APP_WHOSE_IS == `${ApiService.tfsm}`) {
            if (bearer) {
              xhr.setRequestHeader('Authorization', 'Bearer ' + bearer);
            }
            if ((localStorage as any).pStrCookie) {
              xhr.setRequestHeader('pStrCookie', (localStorage as any).pStrCookie);
            }
          } else {
            xhr.withCredentials = true;
            const jwt = JwtService.getToken();
            if (jwt) {
              xhr.setRequestHeader('Authorization', 'Bearer ' + jwt);
            }
          }

          xhr.onload = async function () {
            try {
              if (
                process.env.VUE_APP_WHOSE_IS == `${ApiService.tfsm}` &&
                xhr.status === 401 &&
                allowRetry
              ) {
                OAuthTokenManager.invalidate();
                try {
                  resolve(await sendOnce(false));
                } catch (retryErr) {
                  reject(retryErr);
                }
                return;
              }
              const data = JSON.parse(xhr.responseText);
              resolve({ data });
            } catch (error) {
              reject(new Error('Error en el procesamiento de la respuesta'));
            }
          };

          xhr.onerror = function () {
            reject(new Error('Error de conexión'));
          };

          xhr.send(params);
        } catch (error) {
          reject(new Error('Error en la solicitud'));
        }
      });
    };

    return sendOnce(true);
  }

  static async update(resource, slug, params) {
    try {
      const originalResource = resource;
      const prepared = await ApiService.prepareRequest(originalResource, 'put', slug);
      const buildInit = (headers: any): RequestInit => ({
        ...ApiService.getconfig({ method: 'PUT', headers, ambience: process.env.VUE_APP_WHOSE_IS }),
        redirect: 'error',
      });
      const response = await ApiService.fetchWithOAuthRetry(
        prepared.url,
        buildInit(prepared.headers),
        async () => {
          const again = await ApiService.prepareRequest(originalResource, 'put', slug);
          return { url: again.url, init: buildInit(again.headers) };
        },
      );
      return await ApiService.handleResponse(response);
    } catch (error) {
      throw new Error('Error en la actualización');
    }
  }

  static async put(resource, params, isCustomer = false) {
    try {
      const originalResource = resource;

      if (!isCustomer && JwtService.getToken()) {
        params = { ...params, user_token: JwtService.getToken() };
      }

      const buildOnce = async () => {
        const prepared = await ApiService.prepareRequest(originalResource, 'put');
        return {
          url: prepared.url,
          init: {
            ...ApiService.getconfig({ method: 'PUT', headers: prepared.headers, ambience: process.env.VUE_APP_WHOSE_IS }),
            redirect: 'error' as RequestRedirect,
            body: JSON.stringify(params),
          },
        };
      };

      const first = await buildOnce();
      const response = await ApiService.fetchWithOAuthRetry(first.url, first.init, buildOnce);
      return await ApiService.handleResponse(response);
    } catch (error: any) {
      // Si el error ya tiene la estructura correcta (viene de handleResponse), lo re-lanzamos
      if (error.response?.data) {
        throw error;
      }
      // Solo lanzamos error genérico si no es un error HTTP manejado
      throw new Error('Error en la actualización');
    }
  }

  static async delete(resource, params: any = {}) {
    try {
      const originalResource = resource;
      const buildOnce = async () => {
        const prepared = await ApiService.prepareRequest(originalResource, 'delete');
        const config = {
          ...ApiService.getconfig({ method: 'DELETE', headers: prepared.headers, ambience: process.env.VUE_APP_WHOSE_IS }),
        };
        const { 'Content-Type': _, ...headersWithoutContentType } = config.headers;
        config.headers = headersWithoutContentType;
        return {
          url: prepared.url,
          init: {
            ...config,
            redirect: 'error' as RequestRedirect,
          },
        };
      };

      const first = await buildOnce();
      const response = await ApiService.fetchWithOAuthRetry(first.url, first.init, buildOnce);
      return await ApiService.handleResponse(response);
    } catch (error: any) {
      // Si el error ya tiene la estructura correcta (viene de handleResponse), lo re-lanzamos
      if (error.response?.data) {
        throw error;
      }
      // Solo lanzamos error genérico si no es un error HTTP manejado
      throw new Error('Error en la eliminación');
    }
  }

  /**
   * Valida y sanitiza parámetros de URL para prevenir SSRF
   * @param {string} param - Parámetro a validar
   * @returns {string} - Parámetro sanitizado
   */
  static validateUrlParam(param) {
    if (!param || typeof param !== 'string') {
      throw new Error('Parámetro de URL no válido');
    }
    
    // Remover caracteres peligrosos y validar formato
    const sanitized = param
      .replace(/[<>"']/g, '') // Remover caracteres peligrosos
      .replace(/[\r\n]/g, '') // Remover CRLF
      .trim();
    
    // Validar que no contenga protocolos o caracteres peligrosos
    if (/^[a-zA-Z0-9]+:/.test(sanitized) || sanitized.includes('//')) {
      throw new Error('Parámetro de URL contiene caracteres no permitidos');
    }
    
    // Validar que no contenga caracteres de control
    // eslint-disable-next-line no-control-regex
    if (/[\x00-\x1F\x7F]/.test(sanitized)) {
      throw new Error('Parámetro de URL contiene caracteres de control');
    }
    
    return sanitized;
  }

  /**
   * Local/Docker gateway: el navegador llama a localhost:8000 (o 900x legacy).
   * En producción/demos la validación SSRF sigue bloqueando localhost.
   */
  private static isLocalBrowserApiAllowed(): boolean {
    if (process.env.VUE_APP_ENV === 'develop') {
      return true;
    }
    const apiBase = process.env.VUE_APP_API_URL_TOYOTA || '';
    return (
      apiBase.includes('localhost') ||
      apiBase.includes('127.0.0.1')
    );
  }

  /**
   * Valida una URL final antes de hacer fetch para prevenir SSRF
   * Veracode Flaw ID: CWE-918 - Server-Side Request Forgery (SSRF)
   * Esta validación final previene SSRF verificando que la URL no apunte a hosts internos o privados
   * @param {string} url - URL a validar
   * @returns {string} - URL validada
   */
  static validateUrlForFetch(url: string): string {
    if (!url || typeof url !== 'string') {
      throw new Error('URL no válida para fetch');
    }

    try {
      const urlObj = new URL(url);
      
      // Veracode Flaw ID: CWE-918 - SSRF - Validar protocolo (solo http/https)
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        throw new Error('Protocolo no permitido');
      }

      const allowLocal = ApiService.isLocalBrowserApiAllowed();
      const hostname = urlObj.hostname.toLowerCase();

      // Veracode Flaw ID: CWE-918 - SSRF - Prevenir acceso a localhost/127.0.0.1
      // Excepción: desarrollo local / gateway Docker (VUE_APP_ENV=develop o API en localhost)
      if (!allowLocal) {
        if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '0.0.0.0') {
          throw new Error('Acceso a localhost no permitido');
        }
      }

      // Veracode Flaw ID: CWE-918 - SSRF - Prevenir acceso a IPs privadas
      // Validar rangos de IPs privadas: 10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16
      const ipv4Regex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
      const ipMatch = hostname.match(ipv4Regex);
      if (ipMatch && !allowLocal) {
        const [, a, b] = ipMatch.map(Number);
        // 10.0.0.0/8
        if (a === 10) {
          throw new Error('Acceso a IP privada no permitido');
        }
        // 172.16.0.0/12
        if (a === 172 && b >= 16 && b <= 31) {
          throw new Error('Acceso a IP privada no permitido');
        }
        // 192.168.0.0/16
        if (a === 192 && b === 168) {
          throw new Error('Acceso a IP privada no permitido');
        }
        // 127.0.0.0/8 (localhost)
        if (a === 127) {
          throw new Error('Acceso a localhost no permitido');
        }
        // 169.254.0.0/16 (link-local)
        if (a === 169 && b === 254) {
          throw new Error('Acceso a IP link-local no permitido');
        }
      }

      // Veracode Flaw ID: CWE-918 - SSRF - Validar que no sea un dominio interno
      if (!allowLocal && (hostname.endsWith('.local') || hostname.endsWith('.internal') || hostname.endsWith('.lan'))) {
        throw new Error('Acceso a dominio interno no permitido');
      }

      return urlObj.toString();
    } catch (e) {
      if (e instanceof TypeError) {
        throw new Error('URL no válida para fetch');
      }
      throw e;
    }
  }

  /**
   * Construye una URL de forma segura para prevenir SSRF
   * Veracode Flaw ID: CWE-918 - Server-Side Request Forgery (SSRF)
   * @param {string} baseUrl - URL base (debe ser válida y no manipulable)
   * @param {string} resource - Recurso/ruta relativa
   * @param {string} slug - Parámetro opcional adicional
   * @returns {string} - URL construida de forma segura
   */
  static buildSafeUrl(baseUrl: string, resource: string, slug?: string, fromOrigin?: boolean): string {
    // Validar baseUrl
    if (!baseUrl || typeof baseUrl !== 'string') {
      throw new Error('URL base no válida');
    }

    // Validar que baseUrl sea una URL válida y no contenga caracteres peligrosos
    let baseUrlObj: URL;
    try {
      baseUrlObj = new URL(baseUrl);
      // Veracode Flaw ID: CWE-201 - Preferir HTTPS para transferencia de información sensible
      // Solo permitir http y https (preferir https cuando se envían tokens)
      if (!['http:', 'https:'].includes(baseUrlObj.protocol)) {
        throw new Error('Protocolo no permitido en URL base');
      }
    } catch (e) {
      throw new Error('URL base no válida');
    }

    // Validar resource
    if (!resource || typeof resource !== 'string') {
      throw new Error('Recurso no válido');
    }

    // Sanitizar resource
    let sanitizedResource = resource
      .replace(/[\r\n]/g, '') // Remover CRLF
      .replace(/[<>"']/g, '') // Remover caracteres peligrosos
      .trim();

    // Validar que resource no contenga protocolos (prevenir SSRF)
    if (/^[a-zA-Z0-9]+:/.test(sanitizedResource) || sanitizedResource.includes('://')) {
      throw new Error('Recurso contiene protocolo no permitido');
    }

    // Validar que resource no contenga caracteres de control
    // eslint-disable-next-line no-control-regex
    if (/[\x00-\x1F\x7F]/.test(sanitizedResource)) {
      throw new Error('Recurso contiene caracteres de control');
    }

    // Asegurar que resource comience con /
    if (!sanitizedResource.startsWith('/')) {
      sanitizedResource = '/' + sanitizedResource;
    }

    // Construir URL usando URL API para prevenir SSRF
    let safeUrl: string;
    try {
      // Separar path y query parameters del resource
      const [resourcePath, queryParams] = sanitizedResource.split('?');
      
      // Si baseUrl tiene un path, preservarlo concatenando el resource al path base
      // Esto evita que new URL() reemplace el path base cuando resource comienza con /
      const basePath = baseUrlObj.pathname;
      if (basePath && basePath !== '/' && !basePath.endsWith('/') && !fromOrigin) {
        // Preservar el path base concatenando el resource path
        const fullPath = basePath + resourcePath;
        const urlWithPath = new URL(fullPath, baseUrlObj.origin);
        // Agregar query parameters si existen
        if (queryParams) {
          urlWithPath.search = queryParams;
        }
        safeUrl = urlWithPath.toString();
      } else {
        // Usar URL API para construir la URL de forma segura
        safeUrl = new URL(sanitizedResource, baseUrl).toString();
      }
    } catch (e) {
      throw new Error('Error al construir URL');
    }

    // Si hay slug, validarlo y agregarlo
    if (slug) {
      const sanitizedSlug = ApiService.validateUrlParam(slug);
      // Asegurar que el slug no contenga caracteres peligrosos
      if (sanitizedSlug.includes('/') || sanitizedSlug.includes('\\')) {
        throw new Error('Slug contiene caracteres no permitidos');
      }
      // Agregar slug de forma segura
      safeUrl = safeUrl.replace(/\/$/, '') + '/' + sanitizedSlug;
    }

    // Validar URL final
    try {
      const finalUrl = new URL(safeUrl);
      // Verificar que la URL final tenga el mismo origen que baseUrl (prevenir SSRF)
      if (finalUrl.origin !== baseUrlObj.origin) {
        throw new Error('URL final no coincide con el origen de la URL base');
      }
      // Verificar que el protocolo sea http o https
      if (!['http:', 'https:'].includes(finalUrl.protocol)) {
        throw new Error('Protocolo no permitido en URL final');
      }
      return finalUrl.toString();
    } catch (e) {
      throw new Error('URL final no válida');
    }
  }

  static checkUrl(resource) {
    if (!resource) {
      throw new Error('URL no válida');
    }

    // Validar que resource no contenga caracteres peligrosos
    if (typeof resource === 'string') {
      // Remover caracteres CRLF y otros peligrosos
      resource = resource.replace(/[\r\n]/g, '').trim();
      
      // Validar que no intente hacer SSRF
      if (/^https?:\/\//i.test(resource) || resource.indexOf('://') !== -1) {
        throw new Error('URL contiene protocolo no permitido');
      }
    }

    // develop legacy: reescritura a puertos 900x. Preferir gateway :8000 +
    // VUE_APP_ENV=production (mismo path-routing que Ingress / Turn demos).
    // Si VUE_APP_API_URL_TOYOTA ya apunta al gateway, no remapeamos.
    if (process.env.VUE_APP_ENV === "develop") {
      const configuredBase = process.env.VUE_APP_API_URL_TOYOTA || '';
      const usesGateway =
        configuredBase.includes('localhost:8000') ||
        configuredBase.includes('127.0.0.1:8000');

      if (usesGateway) {
        ApiService.baseUrl = configuredBase;
      } else {
        // Fallback legacy (microservicios publicados en host 900x)
        const routeMappings = [
          ['/seguridad/hasPermission', 'http://localhost:9005/api/v2'],
          ['/leads', 'http://localhost:9010/api/v2'],
          ['/seguridad', 'http://localhost:9005/api/v2'],
          ['/bitacora', 'http://localhost:9001/api/v2'],
          ['/configurations', 'http://localhost:9002/api/v2'],
          ['/agencies', 'http://localhost:9004/api/v2'],
          ['/groups', 'http://localhost:9004/api/v2'],
          ['/vehicles', 'http://localhost:9006/api/v2'],
          ['/banners', 'http://localhost:9006/api/v2'],
          ['/chat', 'http://localhost:9007/api/v2']
        ];

        for (const [route, baseUrl] of routeMappings) {
          if (resource.startsWith(route)) {
            ApiService.baseUrl = baseUrl;
            break;
          }
        }
      }
    }
  }

  static async handleResponse(response) {
    try {
      if (!response.ok) {
        const data = await response.json();

        // Manejo específico de errores HTTP
        if ([422, 406, 404, 400, 409, 415, 429].includes(response.status)) {
          // Preservar la estructura original del error del backend
          throw { response: { data: data } };
        }

        // Manejo de autenticación
        if (response.status === 401) {
          if (!window.localStorage?.getItem('T')) {
            throw { response: { data: { message: data.message } } };
          }

          JwtService.destroyToken();
          JwtService.destroyType();
          JwtService.destroyVerified();
          localStorage.clear();
          window.location.href = "/sign-in";
          throw { response: { data: { message: "No autorizado" } } };
        }

        throw new Error(`Error HTTP: ${response.status}`);
      }

      // Intentar parsear la respuesta como JSON
      // Primero verificar si hay contenido
      const contentType = response.headers.get("content-type");
      const text = await response.text();
      
      // Si hay contenido y parece ser JSON, intentar parsearlo
      if (text && text.trim().length > 0) {
        try {
          // Intentar parsear como JSON
          const data = JSON.parse(text);
          return { data };
        } catch (error) {
          // Si no es JSON válido pero hay contenido, devolver el texto
          // Esto puede pasar si el Content-Type no es application/json
          if (contentType && contentType.includes("application/json")) {
            // Si el Content-Type dice que es JSON pero falló el parseo, devolver null
            return { data: null };
          }
          // Si no es JSON, devolver null
          return { data: null };
        }
      }

      // Si no hay contenido pero el status es 200, considerar éxito
      if (response.status === 200) {
        return { data: { success: true, message: 'Operación completada exitosamente' } };
      }

      return { data: null };
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw error;
      }
      throw new Error('Error en el procesamiento de la respuesta');
    }
  }
}

export default ApiService;
